import type { GridAlignment, GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid';
import {
  gridClasses,
  gridVisibleColumnDefinitionsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { createContext, type ReactNode, useContext } from 'react';

const PINNED_ROW_ID = '__pinned_top__';
const ALIGN_CLASS: Record<GridAlignment, string> = {
  left: gridClasses['cell--textLeft'],
  right: gridClasses['cell--textRight'],
  center: gridClasses['cell--textCenter'],
};

export const PinnedTopRowContext = createContext<GridValidRowModel | undefined>(undefined);

interface PinnedCellColumn {
  align?: GridAlignment;
  computedWidth: number;
  field: string;
  renderCell?: (params: GridRenderCellParams) => ReactNode;
  valueFormatter?: (
    value: unknown,
    row: GridValidRowModel,
    column: unknown,
    api: unknown,
  ) => unknown;
  valueGetter?: (value: unknown, row: GridValidRowModel, column: unknown, api: unknown) => unknown;
}

function PinnedCell({
  column,
  row,
}: {
  column: PinnedCellColumn;
  row: GridValidRowModel;
}): ReactNode {
  const apiRef = useGridApiContext();
  const value =
    column.valueGetter === undefined
      ? row[column.field]
      : column.valueGetter(undefined, row, column, apiRef);
  const formattedValue: unknown =
    column.valueFormatter === undefined ? value : column.valueFormatter(value, row, column, apiRef);
  const params = {
    id: PINNED_ROW_ID,
    field: column.field,
    row,
    colDef: column,
    value,
    formattedValue,
  } as unknown as GridRenderCellParams;

  return (
    // biome-ignore lint/a11y/useSemanticElements: the grid's own cells are divs
    <div
      className={`${gridClasses.cell} ${ALIGN_CLASS[column.align ?? 'left']}`}
      data-field={column.field}
      role="gridcell"
      style={{ minWidth: column.computedWidth, width: column.computedWidth }}
      tabIndex={-1}
    >
      {column.renderCell === undefined ? String(formattedValue ?? '') : column.renderCell(params)}
    </div>
  );
}

export function PinnedTopRow({ position }: { position: 'top' | 'bottom' }): ReactNode {
  const row = useContext(PinnedTopRowContext);
  const apiRef = useGridApiContext();
  const columns = useGridSelector(
    apiRef,
    gridVisibleColumnDefinitionsSelector,
  ) as unknown as PinnedCellColumn[];

  if (position !== 'top' || row === undefined) return null;

  return (
    // biome-ignore-start lint/a11y/useSemanticElements: the grid's own rows are divs
    <div className={`${gridClasses.pinnedRows} ${gridClasses['pinnedRows--top']}`} role="rowgroup">
      <div className={gridClasses.row} role="row" tabIndex={-1}>
        {columns.map((column) => (
          <PinnedCell column={column} key={column.field} row={row} />
        ))}
      </div>
    </div>
    // biome-ignore-end lint/a11y/useSemanticElements: the grid's own rows are divs
  );
}
